import { isAxiosError } from 'axios';
import { Result, err, ok } from 'neverthrow';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore utils is in the parent dir
import axios from '@utils/axios';

import { CarrierCheckInInputs } from 'pages/LoadView/TrackAndTrace/types';
import { EmailMode } from 'types/EmailMode';
import { ApiError } from 'types/api/ApiError';
import { UpdateCarrierCheckInResult } from 'types/api/UpdateCarrierCheckInResult';
import captureException from 'utils/captureException';

export async function updateCarrierCheckInSOPs(
  loadId: number,
  data: CarrierCheckInInputs
): Promise<Result<UpdateCarrierCheckInResult, ApiError>> {
  const {
    freightTrackingId,
    dispatch,
    pickup,
    afterPickup,
    inTransit,
    dropoff,
    afterDropoff,
    podCollection,
  } = data;

  const buildCheckInPayload = (
    checkIn: any,
    includeThreading: boolean = false
  ) => {
    const replyInThread =
      checkIn.emailMode === EmailMode.Reply ||
      checkIn.emailMode === EmailMode.ReplyToSubject;

    const payload: any = {
      checkIn: checkIn.checkIn,
      recipients: checkIn.recipients,
      cc: checkIn.cc,
      bcc: checkIn.bcc,
      subject: checkIn.subject,
      emailDraft: checkIn.emailDraft,
      replyInThread,
    };

    // Include timing fields if they exist
    if (checkIn.sendTime !== undefined) {
      payload.sendTime = checkIn.sendTime;
    }
    if (checkIn.duration !== undefined) {
      payload.duration = Number(checkIn.duration);
    }
    if (checkIn.timeUnit !== undefined) {
      payload.timeUnit = checkIn.timeUnit;
    }
    if (checkIn.frequency !== undefined) {
      payload.frequency = Number(checkIn.frequency);
    }

    // Include threading fields when replyInThread is true
    if (includeThreading && replyInThread) {
      if (checkIn.threadID) {
        payload.threadID = checkIn.threadID;
      }
      if (checkIn.threadItemID) {
        payload.threadItemID = checkIn.threadItemID;
      }
      if (checkIn.threadReferences) {
        payload.threadReferences = checkIn.threadReferences;
      }
      if (checkIn.inReplyTo && checkIn.inReplyTo.length > 0) {
        payload.inReplyTo = checkIn.inReplyTo;
      }
    }

    return payload;
  };

  const requestBody = {
    freightTrackingId,
    dispatch: buildCheckInPayload(dispatch, true),
    pickup: buildCheckInPayload(pickup, true),
    afterPickup: buildCheckInPayload(afterPickup, true),
    inTransit: buildCheckInPayload(inTransit, true),
    dropoff: buildCheckInPayload(dropoff, true),
    afterDropoff: buildCheckInPayload(afterDropoff, true),
    podCollection: buildCheckInPayload(podCollection, true),
  };

  try {
    const resp = await axios.post(`/load/${loadId}/sops/carrier`, requestBody);

    return ok(resp.data);
  } catch (error) {
    captureException(error, { functionName: 'updateCarrierCheckInSOPs' });

    if (!isAxiosError(error)) {
      return err({ message: 'Failed to update Carrier Check-in SOPs' });
    }

    if (error && isAxiosError(error) && error.response?.status === 401) {
      throw error;
    }

    if (error && error.message === 'Extension context invalidated.') {
      throw error;
    }

    return err({
      message:
        error.response?.data.message ||
        'Failed to update Carrier Check-in SOPs',
    });
  }
}
